import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import ProductNav from '../../components/feature/ProductNav';
import { BookingCalendar } from '../home/components/BookingCalendar';
import StarRatingDisplay from '../../components/feature/StarRatingDisplay';
import MobileProductCTABar from '../../components/feature/MobileProductCTABar';

const CANONICAL = 'https://hoosierlifeinsurance.com/final-expense-insurance-quotes-indianapolis';
const SITE_URL = 'https://hoosierlifeinsurance.com';

/* ─── Types ──────────────────────────────────────────────────────────────── */
type FEAge = '50' | '55' | '60' | '65' | '70' | '75' | '80' | '85';
type FECoverage = '5k' | '10k' | '15k' | '20k' | '25k';
type FEHealth = 'level' | 'graded' | 'guaranteed';

interface FEQuoteResult {
  carrier: string;
  amBest: string;
  monthlyRate: number;
  annualRate: number;
  planType: string;
  waitingPeriod: string;
  maxCoverage: number;
  notes: string;
  badge?: string;
}

/* ─── Rate Engine ────────────────────────────────────────────────────────── */
const BASE_FE_RATES: Record<FEAge, Record<FECoverage, number>> = {
  '50': { '5k': 14, '10k': 22, '15k': 32, '20k': 42, '25k': 52 },
  '55': { '5k': 18, '10k': 30, '15k': 44, '20k': 58, '25k': 72 },
  '60': { '5k': 24, '10k': 42, '15k': 62, '20k': 82, '25k': 102 },
  '65': { '5k': 32, '10k': 58, '15k': 86, '20k': 114, '25k': 142 },
  '70': { '5k': 44, '10k': 82, '15k': 122, '20k': 162, '25k': 202 },
  '75': { '5k': 62, '10k': 118, '15k': 176, '20k': 234, '25k': 292 },
  '80': { '5k': 88, '10k': 170, '15k': 254, '20k': 338, '25k': 422 },
  '85': { '5k': 124, '10k': 242, '15k': 362, '20k': 482, '25k': 602 },
};

const COVERAGE_AMOUNTS: Record<FECoverage, number> = {
  '5k': 5000, '10k': 10000, '15k': 15000, '20k': 20000, '25k': 25000,
};

const HEALTH_MULT: Record<FEHealth, number> = {
  level: 1.0,
  graded: 1.35,
  guaranteed: 1.75,
};

function getFEQuotes(age: FEAge, coverage: FECoverage, health: FEHealth): FEQuoteResult[] {
  const base = BASE_FE_RATES[age][coverage];
  const mult = HEALTH_MULT[health];
  const faceAmount = COVERAGE_AMOUNTS[coverage];
  const monthly = Math.round(base * mult);

  if (health === 'guaranteed') {
    return [
      {
        carrier: 'Mutual of Omaha',
        amBest: 'A+',
        monthlyRate: monthly,
        annualRate: monthly * 12,
        planType: 'Guaranteed Issue Whole Life',
        waitingPeriod: '2-year graded benefit',
        maxCoverage: 25000,
        notes: 'No health questions, no medical exam. Guaranteed approval for ages 45–85. Best guaranteed issue rates.',
        badge: 'Best Guaranteed Issue',
      },
      {
        carrier: 'AIG / American General',
        amBest: 'A',
        monthlyRate: Math.round(monthly * 1.05),
        annualRate: Math.round(monthly * 1.05) * 12,
        planType: 'Guaranteed Issue Whole Life',
        waitingPeriod: '2-year graded benefit',
        maxCoverage: 25000,
        notes: 'Guaranteed approval — no health questions. Coverage up to $25K. Strong financial backing.',
      },
      {
        carrier: 'Foresters Financial',
        amBest: 'A',
        monthlyRate: Math.round(monthly * 1.08),
        annualRate: Math.round(monthly * 1.08) * 12,
        planType: 'Guaranteed Issue Whole Life',
        waitingPeriod: '2-year graded benefit',
        maxCoverage: 25000,
        notes: 'Guaranteed issue with member benefits — includes access to legal services and other perks.',
      },
      {
        carrier: 'Gerber Life',
        amBest: 'A',
        monthlyRate: Math.round(monthly * 1.12),
        annualRate: Math.round(monthly * 1.12) * 12,
        planType: 'Guaranteed Issue Whole Life',
        waitingPeriod: '2-year graded benefit',
        maxCoverage: 25000,
        notes: 'Well-known brand — guaranteed issue for ages 50–80. Simple application process.',
      },
      {
        carrier: 'CUNA Mutual',
        amBest: 'A',
        monthlyRate: Math.round(monthly * 1.15),
        annualRate: Math.round(monthly * 1.15) * 12,
        planType: 'Guaranteed Issue Whole Life',
        waitingPeriod: '2-year graded benefit',
        maxCoverage: 20000,
        notes: 'Guaranteed issue — coverage up to $20K. Good option for credit union members.',
      },
    ];
  }

  return [
    {
      carrier: 'Mutual of Omaha',
      amBest: 'A+',
      monthlyRate: monthly,
      annualRate: monthly * 12,
      planType: health === 'level' ? 'Level Benefit Whole Life' : 'Graded Benefit Whole Life',
      waitingPeriod: health === 'level' ? 'No waiting period' : '2-year graded benefit',
      maxCoverage: 40000,
      notes: health === 'level'
        ? 'Best final expense rates — immediate full benefit, no waiting period. Top pick for healthy seniors.'
        : 'Graded benefit — partial payout in years 1–2, full benefit from year 3. For moderate health conditions.',
      badge: 'Best Rate',
    },
    {
      carrier: 'Foresters Financial',
      amBest: 'A',
      monthlyRate: Math.round(monthly * 1.04),
      annualRate: Math.round(monthly * 1.04) * 12,
      planType: health === 'level' ? 'Level Benefit Whole Life' : 'Graded Benefit Whole Life',
      waitingPeriod: health === 'level' ? 'No waiting period' : '2-year graded benefit',
      maxCoverage: 35000,
      notes: 'Competitive rates with member benefits — includes legal services, counseling, and scholarship programs.',
    },
    {
      carrier: 'Transamerica',
      amBest: 'A',
      monthlyRate: Math.round(monthly * 1.08),
      annualRate: Math.round(monthly * 1.08) * 12,
      planType: health === 'level' ? 'Level Benefit Whole Life' : 'Graded Benefit Whole Life',
      waitingPeriod: health === 'level' ? 'No waiting period' : '2-year graded benefit',
      maxCoverage: 50000,
      notes: 'Higher coverage limits — up to $50K available. Good for Indianapolis seniors needing more than $25K.',
    },
    {
      carrier: 'American Amicable',
      amBest: 'A',
      monthlyRate: Math.round(monthly * 1.11),
      annualRate: Math.round(monthly * 1.11) * 12,
      planType: health === 'level' ? 'Level Benefit Whole Life' : 'Graded Benefit Whole Life',
      waitingPeriod: health === 'level' ? 'No waiting period' : '2-year graded benefit',
      maxCoverage: 30000,
      notes: 'Lenient underwriting — good for seniors with moderate health conditions who want level benefit.',
    },
    {
      carrier: 'Royal Neighbors',
      amBest: 'A',
      monthlyRate: Math.round(monthly * 1.14),
      annualRate: Math.round(monthly * 1.14) * 12,
      planType: health === 'level' ? 'Level Benefit Whole Life' : 'Graded Benefit Whole Life',
      waitingPeriod: health === 'level' ? 'No waiting period' : '2-year graded benefit',
      maxCoverage: 25000,
      notes: 'Fraternal benefit society — member benefits included. Good for Indianapolis seniors wanting community connection.',
    },
  ];
}

/* ─── Burial Cost Data ───────────────────────────────────────────────────── */
const BURIAL_COSTS = [
  { item: 'Funeral home services', low: '$2,000', high: '$4,500' },
  { item: 'Casket', low: '$2,000', high: '$10,000' },
  { item: 'Burial plot (Marion County)', low: '$1,500', high: '$5,000' },
  { item: 'Grave opening & closing', low: '$1,000', high: '$2,500' },
  { item: 'Headstone / grave marker', low: '$1,000', high: '$3,500' },
  { item: 'Embalming & preparation', low: '$500', high: '$1,500' },
  { item: 'Viewing / visitation', low: '$400', high: '$1,200' },
  { item: 'Death certificates (10 copies)', low: '$100', high: '$200' },
  { item: 'Obituary', low: '$200', high: '$600' },
  { item: 'Flowers & reception', low: '$500', high: '$2,000' },
];

/* ─── FAQs ───────────────────────────────────────────────────────────────── */
const FAQS = [
  {
    q: 'How much does final expense insurance cost in Indianapolis?',
    a: 'Final expense insurance costs in Indianapolis depend on your age, coverage amount, and health. Here are sample monthly rates for a $10,000 final expense policy: Age 55: $30–$41/month. Age 60: $42–$57/month. Age 65: $58–$78/month. Age 70: $82–$111/month. Age 75: $118–$159/month. Age 80: $170–$230/month. Age 85: $242–$327/month. Final expense insurance is whole life insurance — premiums never increase and coverage never expires. An independent agent compares all available carriers to find your best final expense rate in Indianapolis.',
  },
  {
    q: 'What is final expense insurance and how does it work in Indianapolis?',
    a: 'Final expense insurance is a type of whole life insurance designed to cover end-of-life costs for Indianapolis seniors. Here\'s how it works: Coverage amounts: Typically $5,000–$25,000 — enough to cover funeral, burial, and related expenses. Premiums: Fixed monthly payments that never increase, regardless of age or health changes. Coverage: Lasts your entire life — never expires as long as premiums are paid. Cash value: Builds a small cash value over time that you can borrow against. Payout: The death benefit is paid directly to your beneficiary — they can use it for any purpose. No restrictions: Your family can use the money for funeral costs, medical bills, credit card debt, or any other expenses. Final expense insurance is the most popular life insurance product for Indianapolis seniors because it\'s affordable, easy to qualify for, and provides immediate peace of mind.',
  },
  {
    q: 'What is the difference between level benefit and graded benefit final expense insurance in Indianapolis?',
    a: 'Level benefit and graded benefit are the two main types of final expense insurance available in Indianapolis. Level benefit (immediate coverage): Full death benefit paid from day one. No waiting period. Requires answering health questions — some conditions may disqualify you. Best rates — typically 25–35% cheaper than graded benefit. Best for Indianapolis seniors in reasonably good health. Graded benefit (2-year waiting period): Partial payout in years 1–2 (typically 30–40% in year 1, 70–80% in year 2, 100% from year 3). Easier to qualify — accepts more health conditions. Higher premiums than level benefit. Best for Indianapolis seniors with significant health conditions who can\'t qualify for level benefit. Guaranteed issue (no health questions): Guaranteed approval — no health questions, no medical exam. 2-year waiting period. Highest premiums. Best for Indianapolis seniors with serious health conditions who can\'t qualify for level or graded benefit.',
  },
  {
    q: 'How much does a funeral cost in Indianapolis?',
    a: 'The average funeral cost in Indianapolis ranges from $8,000 to $15,000 for a traditional burial. Here\'s a breakdown of typical Indianapolis funeral costs: Funeral home services: $2,000–$4,500. Casket: $2,000–$10,000. Burial plot (Marion County): $1,500–$5,000. Grave opening and closing: $1,000–$2,500. Headstone/grave marker: $1,000–$3,500. Embalming and preparation: $500–$1,500. Viewing/visitation: $400–$1,200. Death certificates (10 copies): $100–$200. Obituary: $200–$600. Flowers and reception: $500–$2,000. Cremation is significantly less expensive — typically $2,000–$5,000 in Indianapolis. Final expense insurance of $10,000–$15,000 covers most Indianapolis funeral costs, with money left over for other expenses.',
  },
  {
    q: 'Can I get final expense insurance without a medical exam in Indianapolis?',
    a: 'Yes — all final expense insurance policies in Indianapolis are available without a medical exam. Level benefit policies: No medical exam — just answer health questions by phone or online. Approval in 24–48 hours. Graded benefit policies: No medical exam — simplified health questions. Approval in 24–48 hours. Guaranteed issue policies: No medical exam, no health questions — guaranteed approval. Approval in 24–48 hours. Final expense insurance is specifically designed for Indianapolis seniors who may have health conditions that make traditional life insurance difficult to qualify for. The simplified underwriting process makes it accessible to almost everyone.',
  },
  {
    q: 'What health conditions qualify for final expense insurance in Indianapolis?',
    a: 'Most health conditions qualify for some type of final expense insurance in Indianapolis. Level benefit (best rates) — typically accepts: Controlled high blood pressure. Controlled type 2 diabetes. High cholesterol (controlled). Mild COPD. History of cancer (5+ years in remission). Mild heart conditions (no recent surgery). Graded benefit — typically accepts: Recent cancer diagnosis or treatment. Recent heart attack or stroke (within 2 years). Moderate COPD or emphysema. Kidney disease (not on dialysis). Guaranteed issue — accepts everyone: Any health condition. Recent hospitalizations. Currently on dialysis. Oxygen use. Terminal illness (some carriers). An independent agent knows which carriers are most lenient for your specific health conditions — and can find you the best available rate.',
  },
  {
    q: 'What is the best final expense insurance company in Indianapolis?',
    a: 'The best final expense insurance company in Indianapolis depends on your age, health, and coverage needs. For healthy seniors (level benefit): Mutual of Omaha consistently offers the best final expense rates in Indianapolis — competitive premiums, strong financial rating (A+), and excellent customer service. For seniors with health conditions (graded benefit): Foresters Financial and American Amicable offer lenient underwriting and competitive graded benefit rates. For seniors with serious health conditions (guaranteed issue): Mutual of Omaha and AIG/American General offer the best guaranteed issue rates in Indianapolis. For higher coverage amounts ($25K–$50K): Transamerica offers final expense coverage up to $50,000 — more than most carriers. The key: work with an independent agent who compares all available carriers simultaneously. The best carrier for your specific age and health profile can save you $20–$50/month.',
  },
  {
    q: 'How do I apply for final expense insurance in Indianapolis?',
    a: 'Applying for final expense insurance in Indianapolis is simple and fast. Step 1: Call Hoosier Life Insurance at (317) 648-2983 or book a free 15-minute consultation. Step 2: We\'ll ask about your age, coverage needs, and health situation. Step 3: We compare all available carriers simultaneously — some are significantly more competitive than others for your specific age and health profile. Step 4: We submit your application — no medical exam required. Just answer health questions by phone or online. Step 5: Receive your decision — typically 24–48 hours. Step 6: Coverage begins — your family is protected from day one (level benefit) or after the 2-year waiting period (graded/guaranteed issue). The entire process can be completed in one phone call. Most Indianapolis seniors are approved and covered within 48 hours.',
  },
  {
    q: 'Is final expense insurance worth it for Indianapolis seniors?',
    a: 'Final expense insurance is worth it for most Indianapolis seniors for several reasons: Peace of mind: Your family won\'t have to scramble to cover funeral costs during an already difficult time. Affordable: A $10,000 policy costs $42–$82/month for most Indianapolis seniors aged 60–70 — less than many monthly subscriptions. Guaranteed: Premiums never increase and coverage never expires — unlike term life insurance. No restrictions: Your beneficiary can use the money for any purpose — not just funeral costs. Easy to qualify: No medical exam required — most health conditions are accepted. The main question is whether you have other assets to cover final expenses. If you have significant savings or other life insurance, you may not need final expense insurance. But for most Indianapolis seniors without substantial savings, a $10,000–$25,000 final expense policy provides essential protection at an affordable price.',
  },
  {
    q: 'Can I get final expense insurance at age 80 or 85 in Indianapolis?',
    a: 'Yes — final expense insurance is available for Indianapolis seniors at age 80 and 85. Age 80: Most carriers offer final expense insurance up to age 80. Level benefit policies are available for healthy 80-year-olds. Graded and guaranteed issue policies are available for all health conditions. Sample rates for $10,000 coverage at age 80: $170–$230/month. Age 85: Fewer carriers offer coverage at age 85, but options are available. Guaranteed issue policies are the most accessible at age 85. Sample rates for $10,000 coverage at age 85: $242–$327/month. Age 86+: Coverage becomes very limited. Guaranteed issue policies may still be available from some carriers. An independent agent knows which carriers offer the best rates for older Indianapolis seniors and can find coverage regardless of age.',
  },
];

/* ─── Testimonials ───────────────────────────────────────────────────────── */
const TESTIMONIALS = [
  {
    name: 'Dorothy & Harold M.',
    location: 'Southport, Indianapolis',
    quote: 'We\'re both in our 70s and wanted to make sure our kids wouldn\'t have to worry about funeral costs. Benjamin found us $15K each for $122/month combined. The kids don\'t know it yet, but they\'re taken care of.',
    stars: 5,
    coverage: '$15K each',
    age: 'Ages 72 & 74',
  },
  {
    name: 'Patricia L.',
    location: 'Warren Township, Indianapolis',
    quote: 'I have diabetes and high blood pressure. I thought I\'d have to pay through the nose. Benjamin found me a level benefit policy — no waiting period — for $82/month. I\'m 65 and covered for $10K.',
    stars: 5,
    coverage: '$10K level benefit',
    age: 'Age 65',
  },
  {
    name: 'Robert T.',
    location: 'Pike Township, Indianapolis',
    quote: 'Had a heart attack two years ago. Thought I was uninsurable. Benjamin found me a graded benefit policy — after 2 years I\'ll have full coverage. My wife won\'t have to worry about my funeral.',
    stars: 5,
    coverage: '$15K graded benefit',
    age: 'Age 68',
  },
  {
    name: 'Margaret S.',
    location: 'Beech Grove, Indianapolis',
    quote: 'Age 82, COPD, on oxygen. Benjamin found me a guaranteed issue policy — no health questions, approved in 24 hours. $10K coverage for $170/month. My daughter is the beneficiary. Finally at peace.',
    stars: 5,
    coverage: '$10K guaranteed issue',
    age: 'Age 82',
  },
];

/* ─── Component ─────────────────────────────────────────────────────────── */
export default function FinalExpenseInsuranceQuotesIndianapolisPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showBookingModal, setShowBookingModal] = useState(false);

  const [feAge, setFeAge] = useState<FEAge>('65');
  const [feCoverage, setFeCoverage] = useState<FECoverage>('10k');
  const [feHealth, setFeHealth] = useState<FEHealth>('level');
  const [quotes, setQuotes] = useState<FEQuoteResult[] | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const title = 'Final Expense Insurance Quotes Indianapolis IN | Burial Insurance Rates | Hoosier Life Insurance';
    const description = 'Get final expense insurance quotes in Indianapolis, IN. Compare burial insurance rates by age and health. See Indianapolis funeral costs. Call (317) 648-2983.';
    const keywords = 'final expense insurance quotes Indianapolis, burial insurance quotes Indianapolis, final expense insurance Indianapolis Indiana, funeral insurance quotes Indianapolis';
    document.title = title;

    const metaTags = [
      { name: 'description', content: description },
      { name: 'keywords', content: keywords },
      { name: 'last-modified', content: new Date().toISOString() },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: CANONICAL },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://public.readdy.ai/ai/img_res/1b2633ef4931162ed739ccba13863e50.jpg' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'geo.position', content: '39.7684;-86.1581' },
      { name: 'geo.placename', content: 'Indianapolis, Indiana' },
      { name: 'geo.region', content: 'US-IN' },
    ];

    const addedMetas: HTMLMetaElement[] = [];
    metaTags.forEach(tag => {
      const meta = document.createElement('meta');
      if (tag.name) meta.setAttribute('name', tag.name);
      if (tag.property) meta.setAttribute('property', tag.property);
      meta.setAttribute('content', tag.content);
      document.head.appendChild(meta);
      addedMetas.push(meta);
    });

    const linkCanonical = document.createElement('link');
    linkCanonical.rel = 'canonical';
    linkCanonical.href = CANONICAL;
    document.head.appendChild(linkCanonical);

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: title,
        description,
        url: CANONICAL,
        inLanguage: 'en-US',
        dateModified: '2026-04-12T00:00:00Z',
        isPartOf: { '@type': 'WebSite', name: 'Hoosier Life Insurance', url: SITE_URL },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Final Expense Insurance Indianapolis', item: `${SITE_URL}/final-expense-insurance-indianapolis` },
          { '@type': 'ListItem', position: 3, name: 'Final Expense Insurance Quotes Indianapolis', item: CANONICAL },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map(faq => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      },
    ];

    const scriptTag = document.createElement('script');
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(schemas);
    document.head.appendChild(scriptTag);

    return () => {
      addedMetas.forEach(m => { if (document.head.contains(m)) document.head.removeChild(m); });
      if (document.head.contains(linkCanonical)) document.head.removeChild(linkCanonical);
      if (document.head.contains(scriptTag)) document.head.removeChild(scriptTag);
    };
  }, []);

  const ageOptions: FEAge[] = ['50', '55', '60', '65', '70', '75', '80', '85'];
  const coverageOptions: { value: FECoverage; label: string }[] = [
    { value: '5k', label: '$5K' },
    { value: '10k', label: '$10K' },
    { value: '15k', label: '$15K' },
    { value: '20k', label: '$20K' },
    { value: '25k', label: '$25K' },
  ];
  const healthOptions: { value: FEHealth; label: string; desc: string }[] = [
    { value: 'level', label: 'Level Benefit', desc: 'Good health · No waiting period · Best rates' },
    { value: 'graded', label: 'Graded Benefit', desc: 'Some conditions · 2-yr waiting period' },
    { value: 'guaranteed', label: 'Guaranteed Issue', desc: 'Any health · No questions · Guaranteed approval' },
  ];

  const lowestRate = quotes ? Math.min(...quotes.map(q => q.monthlyRate)) : 0;

  return (
    <div className="min-h-screen bg-[#F9F7F4] font-['Inter',sans-serif]">
      <Header />
      <ProductNav />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-2 text-sm">
          <Link to="/" className="text-gray-500 hover:text-[#D97642] transition-colors flex items-center gap-1 whitespace-nowrap">
            <i className="ri-home-4-line text-sm"></i> Home
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <Link to="/final-expense-insurance-indianapolis" className="text-gray-500 hover:text-[#D97642] transition-colors whitespace-nowrap">
            Final Expense Insurance Indianapolis
          </Link>
          <i className="ri-arrow-right-s-line text-gray-300"></i>
          <span className="text-[#1A3A52] font-semibold whitespace-nowrap">Final Expense Quotes</span>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden min-h-[560px]">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://readdy.ai/api/search-image?query=Indianapolis%20Indiana%20senior%20couple%20in%20their%20late%2060s%20sitting%20at%20modern%20home%20kitchen%20table%20reviewing%20final%20expense%20insurance%20documents%20warm%20contemporary%20home%20environment%20Marion%20County%20Indiana%20natural%20light%20relaxed%20confident%20financial%20planning%20family%20protection%20photorealistic%20warm%20neutral%20tones%20beige%20cream%20background%20peaceful%20serene&width=1440&height=700&seq=fe-quotes-indy-hero-2025a&orientation=landscape"
            alt="Final Expense Insurance Quotes Indianapolis Indiana — Burial Insurance Rates"
            title="Final Expense Insurance Quotes in Indianapolis, Indiana"
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width={1440}
            height={700}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A52]/96 via-[#1A3A52]/85 to-[#1A3A52]/30"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 md:py-28 w-full">
          <div className="max-w-[720px]">
            <span className="inline-flex items-center gap-2 bg-[#D97642]/20 border border-[#D97642]/40 text-[#F4A97A] text-xs font-semibold tracking-widest uppercase px-4 py-1.5 rounded-full mb-6">
              <i className="ri-heart-line text-sm"></i>
              Indianapolis &amp; Marion County · Final Expense Insurance · Ages 50–85 · No Medical Exam
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
              Final Expense Insurance Quotes —{' '}
              <span className="text-[#D97642]">Senior Coverage from $28/mo, Indianapolis</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-6 max-w-[640px]">
              Compare final expense insurance quotes from the top carriers in Indianapolis. Use our <strong className="text-white">Final Expense Quote Finder</strong> to see burial insurance rates by age and health — no medical exam required.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              {[
                { value: '$8–15K', label: 'Average Indianapolis funeral cost — covered by final expense insurance' },
                { value: 'Ages 50–85', label: 'Coverage available for all ages — no medical exam required' },
                { value: '24–48 hrs', label: 'Typical approval time — fast, simple application' },
                { value: 'Free', label: 'Final expense quote comparison — no obligation' },
              ].map(item => (
                <div key={item.label} className="bg-white/15 backdrop-blur-sm border border-white/25 rounded-xl px-4 py-3 text-center">
                  <div className="text-xl font-extrabold text-[#D97642]">{item.value}</div>
                  <div className="text-xs text-white/80 mt-0.5">{item.label}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <a href="#quote-finder" className="inline-flex items-center gap-2 px-7 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-search-line text-lg"></i>
                Get My Final Expense Quote
              </a>
              <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-7 py-4 bg-white/20 border border-white/40 text-white text-base font-semibold rounded-xl hover:bg-white/30 transition-all cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-lg"></i>
                (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#1A3A52] py-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
            {[
              { value: '$11,000', label: 'Average Indianapolis funeral cost in 2026 — final expense insurance covers it all' },
              { value: 'No Exam', label: 'No medical exam required — just answer a few health questions (or none at all)' },
              { value: 'Lifetime', label: 'Coverage that never expires — premiums locked in and never increase' },
              { value: '5+', label: 'Top carriers compared — some are 20–30% cheaper than others for the same coverage' },
            ].map(stat => (
              <div key={stat.label} className="flex flex-col items-center gap-1">
                <span className="text-2xl md:text-3xl font-extrabold text-[#D97642]">{stat.value}</span>
                <span className="text-xs text-white/70 leading-snug max-w-[160px]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Takeaways */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-checkbox-circle-fill text-[#D97642] text-2xl"></i>
            </div>
            <div className="flex-1">
              <h2 className="text-[#1A3A52] font-bold text-base mb-3">Key Facts: Final Expense Insurance Quotes in Indianapolis</h2>
              <ul className="grid md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Final expense insurance is whole life insurance — premiums never increase and coverage never expires',
                  'No medical exam required — level, graded, and guaranteed issue options available for all health conditions',
                  'Coverage from $5,000–$50,000 — enough to cover Indianapolis funeral costs and leave money for family',
                  'Level benefit policies pay full death benefit from day one — no waiting period for healthy seniors',
                  'Guaranteed issue policies accept everyone — no health questions, approved in 24–48 hours',
                  'Carrier selection matters — some carriers are 20–30% cheaper than others for the same age and coverage',
                ].map((bullet, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-600 text-sm">
                    <i className="ri-arrow-right-s-line text-[#D97642] flex-shrink-0 mt-0.5"></i>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 text-gray-400 text-xs">Last reviewed April 2026 · Licensed Life Insurance Specialists at Hoosier Life Insurance · Indiana Licensed · Independent — 20+ Carriers</div>
            </div>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="max-w-7xl mx-auto px-6 py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 xl:gap-16 items-start">

          {/* LEFT */}
          <div>

            {/* QUOTE FINDER */}
            <div id="quote-finder" className="mb-16 scroll-mt-24">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Final Expense Quote Finder</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-3 leading-tight">
                Compare Final Expense Insurance Quotes by Age &amp; Health — Indianapolis
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                Select your age, coverage amount, and health/plan type to see estimated final expense rates from the top carriers in Indianapolis — ranked lowest to highest.
              </p>

              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-[#1A3A52] px-7 py-7">
                  <h3 className="text-base font-bold text-white mb-6 flex items-center gap-2">
                    <i className="ri-heart-line text-[#D97642]"></i>
                    Your Profile
                  </h3>

                  {/* Age */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Your Age</label>
                    <div className="flex flex-wrap gap-2">
                      {ageOptions.map(a => (
                        <button key={a} onClick={() => { setFeAge(a); setQuotes(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${feAge === a ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {a}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Coverage */}
                  <div className="mb-6">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Coverage Amount</label>
                    <div className="flex flex-wrap gap-2">
                      {coverageOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setFeCoverage(opt.value); setQuotes(null); }}
                          className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${feCoverage === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Health / Plan Type */}
                  <div className="mb-7">
                    <label className="text-xs font-semibold text-white/70 uppercase tracking-wide mb-3 block">Plan Type / Health</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {healthOptions.map(opt => (
                        <button key={opt.value} onClick={() => { setFeHealth(opt.value); setQuotes(null); }}
                          className={`px-4 py-4 rounded-xl text-left transition-all cursor-pointer ${feHealth === opt.value ? 'bg-[#D97642] text-white' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}>
                          <div className="text-sm font-bold">{opt.label}</div>
                          <div className={`text-xs mt-1 leading-snug ${feHealth === opt.value ? 'text-white/80' : 'text-white/50'}`}>{opt.desc}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <button onClick={() => setQuotes(getFEQuotes(feAge, feCoverage, feHealth))}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold text-base">
                    <i className="ri-search-line text-lg"></i>
                    Find My Final Expense Quote
                  </button>
                </div>

                {/* Results */}
                {quotes !== null && (
                  <div className="px-7 py-7">
                    <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
                      <h3 className="text-base font-bold text-[#1A3A52]">
                        Final Expense Quotes — ${COVERAGE_AMOUNTS[feCoverage].toLocaleString()} · Age {feAge} · {healthOptions.find(h => h.value === feHealth)?.label}
                      </h3>
                      <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#4A7C59]/10 text-[#4A7C59] whitespace-nowrap">
                        From ${lowestRate}/mo
                      </span>
                    </div>

                    <div className="flex flex-col gap-3 mb-6">
                      {quotes.map((q, i) => (
                        <div key={q.carrier} className={`rounded-xl border p-5 ${i === 0 ? 'border-[#4A7C59]/30 bg-[#F0FFF4]' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              <div className={`w-8 h-8 flex items-center justify-center rounded-full text-white text-sm font-bold flex-shrink-0 mt-0.5 ${i === 0 ? 'bg-[#4A7C59]' : 'bg-[#1A3A52]/30'}`}>{i + 1}</div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                  <span className="text-sm font-bold text-[#1A3A52]">{q.carrier}</span>
                                  {q.badge && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#4A7C59] text-white whitespace-nowrap">{q.badge}</span>}
                                  <span className="text-xs bg-[#1A3A52]/10 text-[#1A3A52] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">AM Best {q.amBest}</span>
                                </div>
                                <p className="text-xs text-gray-500 font-medium mb-1">{q.planType}</p>
                                <p className="text-xs text-gray-400 leading-relaxed mb-1">{q.notes}</p>
                                <div className="flex items-center gap-3 flex-wrap">
                                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${q.waitingPeriod === 'No waiting period' ? 'bg-[#4A7C59]/10 text-[#4A7C59]' : 'bg-amber-50 text-amber-600'}`}>
                                    {q.waitingPeriod}
                                  </span>
                                  <span className="text-xs text-gray-400">Max: ${q.maxCoverage.toLocaleString()}</span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <div className={`text-xl font-extrabold ${i === 0 ? 'text-[#4A7C59]' : 'text-[#1A3A52]'}`}>${q.monthlyRate}<span className="text-sm font-normal">/mo</span></div>
                              <div className="text-xs text-gray-400">${q.annualRate.toLocaleString()}/yr</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-[#1A3A52]/5 rounded-xl p-4 mb-5">
                      <p className="text-xs text-gray-500 leading-relaxed">
                        <strong className="text-[#1A3A52]">Disclaimer:</strong> Rates are estimates for illustration purposes. Actual rates depend on full underwriting and carrier selection. Call <a href="tel:+13176482983" className="text-[#D97642] font-semibold">(317) 648-2983</a> for your exact personalized final expense quote.
                      </p>
                    </div>

                    <button onClick={() => setShowBookingModal(true)}
                      className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                      <i className="ri-calendar-check-line text-lg"></i>
                      Get My Exact Final Expense Quote — Free 15-Min Call
                    </button>
                  </div>
                )}

                {quotes === null && (
                  <div className="px-7 py-10 text-center">
                    <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#D97642]/10 mx-auto mb-4">
                      <i className="ri-heart-line text-3xl text-[#D97642]"></i>
                    </div>
                    <p className="text-sm text-gray-500">Select your age, coverage, and plan type above, then click <strong className="text-[#1A3A52]">Find My Final Expense Quote</strong>.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Indianapolis Burial Costs */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Indianapolis Funeral Costs</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-4 leading-tight">
                How Much Does a Funeral Cost in Indianapolis?
              </h2>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                The average funeral in Indianapolis costs $8,000–$15,000. Here's a detailed breakdown of typical costs in Marion County so you can choose the right coverage amount.
              </p>
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="bg-[#1A3A52] px-6 py-4 grid grid-cols-3 gap-2 text-xs font-semibold text-white/70 uppercase tracking-wide">
                  <div className="col-span-1">Expense</div>
                  <div className="text-center">Low Estimate</div>
                  <div className="text-center">High Estimate</div>
                </div>
                <div className="divide-y divide-gray-100">
                  {BURIAL_COSTS.map((row, i) => (
                    <div key={i} className={`px-6 py-4 grid grid-cols-3 gap-2 items-center ${i % 2 === 0 ? 'bg-white' : 'bg-[#F9F7F4]'}`}>
                      <div className="text-sm text-[#1A3A52] font-medium">{row.item}</div>
                      <div className="text-center text-sm text-gray-500">{row.low}</div>
                      <div className="text-center text-sm font-semibold text-[#D97642]">{row.high}</div>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-4 bg-[#D97642]/5 border-t border-[#D97642]/20">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-sm font-bold text-[#1A3A52]">Total Estimated Cost</span>
                    <div className="flex gap-6">
                      <div className="text-center">
                        <div className="text-xs text-gray-400">Low</div>
                        <div className="text-base font-extrabold text-[#1A3A52]">$9,200</div>
                      </div>
                      <div className="text-center">
                        <div className="text-xs text-gray-400">High</div>
                        <div className="text-base font-extrabold text-[#D97642]">$31,000</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-3">Estimates for Marion County, Indianapolis. Cremation typically costs $2,000–$5,000. Source: National Funeral Directors Association 2026 data.</p>
            </div>

            {/* Testimonials */}
            <div className="mb-14">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">Real Stories</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Indianapolis Seniors Who Got Final Expense Coverage
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {TESTIMONIALS.map(t => (
                  <div key={t.name} className="bg-white rounded-xl p-6 border border-gray-100">
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className="bg-[#D97642]/10 text-[#D97642] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.coverage}</span>
                      <span className="bg-[#1A3A52]/10 text-[#1A3A52] text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">{t.age}</span>
                    </div>
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#D97642] text-sm"></i>)}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-4 italic">"{t.quote}"</p>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#1A3A52]/10">
                        <i className="ri-user-fill text-sm text-[#1A3A52]"></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-[#1A3A52]">{t.name}</p>
                        <p className="text-xs text-gray-400">{t.location}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ */}
            <div id="faq">
              <span className="text-xs font-semibold tracking-widest text-[#D97642] uppercase mb-3 block">FAQ</span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#1A3A52] mb-8 leading-tight">
                Final Expense Insurance Quotes Indianapolis — Frequently Asked Questions
              </h2>
              <div className="flex flex-col gap-3">
                {FAQS.map((faq, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                    <button onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-gray-50 transition-colors">
                      <h3 className="text-sm font-bold text-[#1A3A52] pr-4 leading-snug">
                        <a id={`faq-fe-quotes-indy-${i}`}>{faq.q}</a>
                      </h3>
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${openFAQ === i ? 'ri-subtract-line' : 'ri-add-line'} text-lg text-[#D97642]`}></i>
                      </div>
                    </button>
                    {openFAQ === i && (
                      <div className="px-6 pb-5">
                        <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:sticky lg:top-24 flex flex-col gap-6">
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-heart-fill text-xl text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white leading-tight">Free Final Expense Quote</h3>
                    <p className="text-xs text-white/70">5+ carriers compared · No obligation</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ No medical exam</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ Ages 50–85</span>
                  <span className="bg-white/10 text-white/90 text-xs font-medium px-3 py-1 rounded-full whitespace-nowrap">✓ All health conditions</span>
                </div>
              </div>
              <div className="px-7 py-7">
                <div className="flex flex-col gap-3">
                  <button onClick={() => setShowBookingModal(true)}
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#D97642] text-white rounded-xl hover:bg-[#C26638] transition-all cursor-pointer font-bold">
                    <i className="ri-calendar-check-line text-lg"></i>
                    Book Free Consultation
                  </button>
                  <a href="tel:+13176482983"
                    className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#1A3A52] text-white rounded-xl hover:bg-[#0F2A3F] transition-all cursor-pointer font-bold">
                    <i className="ri-phone-fill text-lg"></i>
                    Call (317) 648-2983
                  </a>
                </div>
                <p className="text-xs text-gray-400 text-center mt-4">Free · No obligation · All ages &amp; health conditions</p>
              </div>
            </div>

            {/* Rate by Age */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4 flex items-center gap-2">
                <i className="ri-price-tag-3-line text-[#D97642]"></i>
                Final Expense Rates by Age — $10K Level Benefit
              </h3>
              <div className="flex flex-col gap-2">
                {[
                  { age: 'Age 55', rate: '$30/mo', annual: '$360/yr' },
                  { age: 'Age 60', rate: '$42/mo', annual: '$504/yr' },
                  { age: 'Age 65', rate: '$58/mo', annual: '$696/yr' },
                  { age: 'Age 70', rate: '$82/mo', annual: '$984/yr' },
                  { age: 'Age 75', rate: '$118/mo', annual: '$1,416/yr' },
                  { age: 'Age 80', rate: '$170/mo', annual: '$2,040/yr' },
                ].map((row, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                    <span className="text-xs font-semibold text-[#1A3A52]">{row.age}</span>
                    <div className="flex gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Monthly</div>
                        <div className="text-xs font-bold text-[#D97642]">{row.rate}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400">Annual</div>
                        <div className="text-xs font-bold text-[#1A3A52]">{row.annual}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-3">Level benefit, standard health. Rates vary by carrier.</p>
            </div>

            {/* Booking Calendar */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="bg-[#1A3A52] px-7 py-5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#D97642]/20">
                    <i className="ri-calendar-check-fill text-lg text-[#D97642]"></i>
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-white leading-tight">Free 15-Min Final Expense Call</h3>
                    <p className="text-xs text-white/70">All carriers compared · No pressure</p>
                  </div>
                </div>
              </div>
              <div className="px-7 py-6">
                <BookingCalendar
                  timeslotsApi="https://readdy.ai/api/public/calendar/timeslots/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  appointmentsApi="https://readdy.ai/api/public/calendar/appointments/9395c896-070b-4356-87d5-6717161b747b.670457f5037855118780bb2c7e1f4bd822f6755285fc48fb9798eacba5906de1"
                  texts={{
                    heading: 'Book Your Final Expense Call',
                    step0Title: 'Pick a Date', step1Title: 'Choose a Time', step2Title: 'Confirm Your Spot',
                    selectDate: 'Select Date', selectTime: 'See Available Times →', reviewAndBook: 'Confirm Booking',
                    previousMonth: 'Prev', nextMonth: 'Next', noSlotsAvailable: 'No slots available',
                    selectedDateTime: 'Your Appointment', modifyTime: 'Change',
                    customerName: 'Full Name', customerPhone: 'Phone Number',
                    notes: 'Age, health conditions, coverage goal? (Optional)',
                    submitButton: 'Confirm My Free Final Expense Call', submitting: 'Booking...',
                    successMessage: "You're booked! A confirmation text is on its way.",
                    errorMessage: 'Booking failed. Please try again.', backButton: '← Back to Calendar', required: '*',
                  }}
                  containerStyle=""
                />
              </div>
            </div>

            {/* Related Links */}
            <div className="bg-white rounded-xl border border-gray-100 p-6">
              <h3 className="text-sm font-bold text-[#1A3A52] mb-4">Related Pages</h3>
              <div className="flex flex-col gap-2">
                {[
                  { to: '/final-expense-insurance-indianapolis', label: 'Final Expense Insurance Indianapolis', icon: 'ri-heart-line' },
                  { to: '/burial-insurance-indianapolis', label: 'Burial Insurance Indianapolis', icon: 'ri-leaf-line' },
                  { to: '/senior-life-insurance-indianapolis', label: 'Senior Life Insurance Indianapolis', icon: 'ri-user-heart-line' },
                  { to: '/no-exam-life-insurance-indianapolis', label: 'No Medical Exam Life Insurance', icon: 'ri-timer-flash-line' },
                ].map(link => (
                  <Link key={link.to} to={link.to} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F9F7F4] transition-colors group">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#D97642]/10 flex-shrink-0">
                      <i className={`${link.icon} text-sm text-[#D97642]`}></i>
                    </div>
                    <span className="text-sm text-gray-600 group-hover:text-[#1A3A52] font-medium">{link.label}</span>
                    <i className="ri-arrow-right-s-line text-gray-300 ml-auto group-hover:text-[#D97642]"></i>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-[#1A3A52] py-14">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
            Protect your family from Indianapolis funeral costs — starting at $30/month.
          </h2>
          <p className="text-base text-white/75 mb-7">
            Use our Final Expense Quote Finder above, then call for your exact personalized quote. No medical exam required. Approved in 24–48 hours. No obligation.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#quote-finder" className="inline-flex items-center gap-2 px-8 py-4 bg-[#D97642] text-white text-base font-bold rounded-xl hover:bg-[#C26638] transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-search-line text-lg"></i>
              Get My Final Expense Quote
            </a>
            <a href="tel:+13176482983" className="inline-flex items-center gap-2 px-8 py-4 bg-white/15 border border-white/30 text-white text-base font-semibold rounded-xl hover:bg-white/25 transition-all cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-lg"></i>
              (317) 648-2983
            </a>
          </div>
        </div>
      </section>

      <StarRatingDisplay rating={4.9} reviewCount={127} productName="Final Expense Insurance Quotes Indianapolis" />
      <Footer />
      <MobileProductCTABar productName="Final Expense Insurance" quoteHref="/instant-quote" />

      {showBookingModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-[#1A3A52]">Get Your Final Expense Insurance Quote — Indianapolis</h3>
              <button onClick={() => setShowBookingModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                <i className="ri-close-line text-3xl"></i>
              </button>
            </div>
            <div className="p-6">
              <iframe src="https://api.leadconnectorhq.com/widget/bookings/hoosier-life-insurance" style={{ width: '100%', height: '600px', border: 'none' }} title="Book Appointment" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
